import React, { ComponentType, useMemo } from 'react'
import PrizeBadge from '../../../../components/ScopedBlock/PrizeBadge.svg?url'
import { Tabs } from '../../../../components/Tabs/Tabs'
import { Spinner, Tab } from 'react-bootstrap'
import { Modal } from '../../../../components/Modal/Modal'
import { AiFeaturePreset } from '../../../../models/AiFeaturePreset'
import { InputParametersHierarchyInfo } from './InputParametersHierarchyInfo'
import { useInputParametersConfig } from '../../../../components/InputParameterHierarchy/hooks'
import { InputParameterHierarchyType, MeetingTemplateInputParametersHierarchyConfigComponentProps } from '../../../../components/InputParameterHierarchy/model'
import { useGlobalFormPresetInputParameters } from './hooks'

export interface PresetInputParametersHierarchyConfigModalProps {
  show: boolean
  onHide: () => void
  preset: AiFeaturePreset
  GlobalConfig: ComponentType<MeetingTemplateInputParametersHierarchyConfigComponentProps>
  CurrentUserConfig: ComponentType<MeetingTemplateInputParametersHierarchyConfigComponentProps>
  TeamMembersConfig?: ComponentType<MeetingTemplateInputParametersHierarchyConfigComponentProps>
}

export const PresetInputParametersHierarchyConfigModal: React.FC<PresetInputParametersHierarchyConfigModalProps> = ({
  show,
  onHide,
  preset,
  GlobalConfig,
  CurrentUserConfig,
  TeamMembersConfig,
}) => {
  const { ipHierarchyEnabled, hasIpHierarchyAdminScope, showTabs, teamMembersLoading, hasTeamMembers } = useInputParametersConfig()
  const { presetInputParameters } = useGlobalFormPresetInputParameters(preset)

  const configProps = useMemo(() => {
    return {
      type: InputParameterHierarchyType.AI_FEATURE_PRESET,
      typeId: preset.id,
      inputParametersSchema: preset.inputParametersSchema,
      defaultInputParameters: presetInputParameters,
      onHide,
      showCustomizationSetting: hasTeamMembers || (hasIpHierarchyAdminScope && ipHierarchyEnabled),
      ipHierarchyEnabled,
    }
  }, [preset, onHide, hasIpHierarchyAdminScope, ipHierarchyEnabled, hasTeamMembers])

  const userTitle = (
    <div className='d-flex align-items-center'>
      {!ipHierarchyEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Your Own
    </div>
  )

  const teamMembersTitle = (
    <div className='d-flex align-items-center'>
      {!ipHierarchyEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Your Team Members
    </div>
  )

  const tabConfig = (
    <Tabs defaultActiveKey={hasIpHierarchyAdminScope ? 'global' : 'currentUser'} id='preset-config-tabs' className='d-flex mb-3' mountOnEnter unmountOnExit>
      <Tab eventKey='global' title='Account Level'>
        <InputParametersHierarchyInfo icon='bi-exclamation-square' message='These settings will apply to everyone unless they have their own settings.' />
        <GlobalConfig {...configProps} />
      </Tab>
      <Tab eventKey='currentUser' title={userTitle}>
        <InputParametersHierarchyInfo icon='bi-info-square' message='These settings will only apply to you.' />
        <CurrentUserConfig {...configProps} />
      </Tab>
      {TeamMembersConfig && (
        <Tab eventKey='teamMembers' title={teamMembersTitle}>
          <InputParametersHierarchyInfo icon='bi-info-square' message='Manage settings for your team members.' />
          <TeamMembersConfig {...configProps} />
        </Tab>
      )}
    </Tabs>
  )

  const singleConfig = ipHierarchyEnabled ? <CurrentUserConfig {...configProps} /> : <GlobalConfig {...configProps} />

  const body = (
    <>
      <h4 className='text-center mb-5'>{`Customize ${preset.name}`}</h4>
      {teamMembersLoading ? (
        <div className='d-flex align-items-center justify-content-center p-2'>
          <Spinner size={'sm'} />
        </div>
      ) : showTabs ? (
        tabConfig
      ) : (
        singleConfig
      )}
    </>
  )

  return <Modal size='lg' show={show} onHide={onHide} header={<></>} body={body} footer={<></>} />
}
